import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  ActiveItemsModule,
  ItemPricingTableModule,
  ItemSelectionCartModule,
  ItemSelectorModule,
  ServicesModule,
} from '@galaxy/inventory-ui';
import { LexiconModule } from '@galaxy/lexicon';
import { TranslateModule } from '@ngx-translate/core';
import { BillingUiModule } from '@vendasta/billing-ui';
import { ProductActivationPrereqFormModule } from '@vendasta/businesses';
import { VaFormsModule } from '@vendasta/forms';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { GalaxyStickyFooterModule } from '@vendasta/galaxy/sticky-footer';
import { OrdersModule } from '@vendasta/orders';
import { SalesOrderFormModule } from '@vendasta/sales-ui';
import { StencilsModule, VaPricingModule, StoreModule as VendastaStoreModule } from '@vendasta/store';
import { IconComponent, VaIconModule } from '@vendasta/uikit';
import { WorkOrderFormComponent } from '@vendasta/work-order';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import baseTranslation from '../../assets/i18n/en_devel.json';
import { PaginatedAppActivationTableComponent } from '../common/app-selection/paginated-app-activation-table/paginated-app-activation-table.component';
import { BannerComponent } from '../common/banner/banner.component';
import { PageComponent } from '../common/page.component';
import { UserDisplayNamePipe } from '../common/pipes/user-display-name.pipe';
import { TablePricingComponent } from '../common/table-pricing/table-pricing.component';
import { UnifiedTOSDialogComponent } from '../common/tos-unified/unified-tos-dialog.component';
import { VDialogComponent } from '../common/v-dialog.component';
import { WEBLATE_COMPONENT_NAME } from '../constants';
import { PricingDetailsModule } from '../marketplace-app/pricing-details';
import { PreviewEmailDialogModule } from '../preview-email-dialog/preview-email-dialog.module';
import { ContactInfoComponent } from '../shared-billing/settings/contact-info.component';
import { PaymentInfoComponent } from '../shared-billing/settings/payment-info.component';
import { VbcHostService } from '../vbc/vbc.host.service';
import { ActivationRoutingModule } from './activation-routing.module';
import { ActivationSettingsApiService } from './activation-settings-api.service';
import { ActivationMessageDialogComponent } from './confirmation-page/activation-message-dialog/activation-message-dialog.component';
import { ActivationPriceSummaryComponent } from './confirmation-page/activation-price-summary/activation-price-summary.component';
import { ActivationPriceComponent } from './confirmation-page/activation-price/activation-price.component';
import { ChangePriceDialogComponent } from './confirmation-page/change-price-dialog/change-price-dialog.component';
import { CollectPaymentComponent } from './confirmation-page/collect-payment/collect-payment.component';
import { OrderConfirmationPageComponent } from './confirmation-page/order-confirmation-page/order-confirmation-page.component';
import { OrderConfirmationService } from './confirmation-page/order-confirmation.service';
import { OrderExcessComponent } from './confirmation-page/order-excess/order-excess.component';
import { OrderSummaryComponent } from './confirmation-page/order-summary/order-summary.component';
import { PriceOverridePipe } from './confirmation-page/price-override.pipe';
import { StairstepOrderService } from './confirmation-page/stairstep-order.service';
import { ActivationNavigationService } from './core/activation-navigation.service';
import { ProductOrderFormStoreService } from './core/product-order-form.store.service';
import { DynamicOrderFormPageComponent } from './dynamic-order-form-page/dynamic-order-form-page.component';
import { DynamicOrderFormService } from './dynamic-order-form-page/dynamic-order-form.service';
import { TermsOfServiceDialogComponent } from './dynamic-order-form-page/terms-of-service-dialog.component';
import { OrderFormPageComponent, ProductOrderFormService } from './order-form-page';
import { AppsCustomPriceFormComponent } from './order-form-page/order-form-page/apps-custom-price-form/apps-custom-price-form.component';
import { AppsCustomPriceComponent } from './order-form-page/order-form-page/apps-custom-price/apps-custom-price.component';
import { OrderSubmittedPageComponent } from './order-submitted-page/order-submitted-page.component';
import {
  ActivatablePackageItemsRowComponent,
  ActivatablePackageRowComponent,
  ActivationCartComponent,
  ActivationSelectionPageComponent,
  PackageActivationTableComponent,
  PaginatedPackageActivationTableComponent,
} from './selection-page';
import { CustomProductRecommendationsComponent } from './selection-page/custom-product-recommendations/custom-product-recommendations.component';
import { SharedModule } from './shared/shared.module';

export const MODULE_IMPORTS = [
  BannerComponent,
  ActivationRoutingModule,
  SharedModule,
  ContactInfoComponent,
  PaymentInfoComponent,
  MatTooltipModule,
  MatDialogModule,
  MatExpansionModule,
  MatChipsModule,
  GalaxyPageModule,
  StencilsModule,
  PreviewEmailDialogModule,
  PricingDetailsModule,
  TranslateModule,
  LexiconModule.forChild({
    componentName: WEBLATE_COMPONENT_NAME,
    baseTranslation: baseTranslation,
  }),
  CommonModule,
  InfiniteScrollModule,
  VendastaStoreModule,
  SalesOrderFormModule,
  ProductActivationPrereqFormModule,
  VaIconModule,
  OrdersModule,
  MatTableModule,
  MatFormFieldModule,
  MatInputModule,
  MatCheckboxModule,
  MatSelectModule,
  MatDatepickerModule,
  VaFormsModule,
  VaPricingModule,
  GalaxyButtonLoadingIndicatorModule,
  BillingUiModule,
  GalaxyAlertModule,
  ItemSelectorModule,
  ItemSelectionCartModule,
  ItemPricingTableModule,
  ServicesModule,
  WorkOrderFormComponent,
  UnifiedTOSDialogComponent,
  VDialogComponent,
  GalaxyStickyFooterModule,
  ActiveItemsModule,
  UserDisplayNamePipe,
];

export const MODULE_DECLARATIONS = [
  ActivationSelectionPageComponent,
  ActivationCartComponent,
  PackageActivationTableComponent,
  ActivatablePackageRowComponent,
  ActivatablePackageItemsRowComponent,
  PaginatedPackageActivationTableComponent,
  OrderFormPageComponent,
  DynamicOrderFormPageComponent,
  TermsOfServiceDialogComponent,
  ActivationPriceComponent,
  OrderConfirmationPageComponent,
  OrderSubmittedPageComponent,
  PriceOverridePipe,
  OrderSummaryComponent,
  ActivationMessageDialogComponent,
  OrderExcessComponent,
  CustomProductRecommendationsComponent,
  ChangePriceDialogComponent,
  AppsCustomPriceComponent,
  AppsCustomPriceFormComponent,
  CollectPaymentComponent,
  ActivationPriceSummaryComponent,
];
@NgModule({
  imports: [MODULE_IMPORTS, PageComponent, TablePricingComponent, IconComponent, PaginatedAppActivationTableComponent],
  declarations: MODULE_DECLARATIONS,
  providers: [
    ActivationNavigationService,
    ProductOrderFormService,
    ProductOrderFormStoreService,
    DynamicOrderFormService,
    OrderConfirmationService,
    StairstepOrderService,
    ActivationSettingsApiService,
  ],
})
export class ActivationModule {}

export function getHostName(vbcHostService: VbcHostService): string {
  return vbcHostService.hostWithScheme();
}
